import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject, SubscriptionLike, finalize, map, of, startWith } from 'rxjs';
import { completeSubject, objectValue } from 'src/app/common/app.helpers';
import FieldsConstants from 'src/app/common/fields.constants';
import { CommunitiesService } from 'src/app/components/community/communities.service';
import { ProjectsService } from 'src/app/components/projects/projects.service';
import { SearchCategories, SearchStrategyTypes } from 'src/app/components/search/search.enum';
import { SearchService } from 'src/app/components/search/search.service';
import {
  DropdownFieldsProcessorKind,
  FieldsSearchStrategy,
  INITIAL_PAGINATION_STATE,
  SearchParameterOptions,
  accumulateResult,
} from 'src/app/forms/fields/components/selector-options.helpers';
import { FieldAttribute, OptionsChangesContent, SelectorOption } from 'src/app/interfaces/form-and-field.interface';
import { PaginationState } from 'src/app/interfaces/pagination.interface';
import {
  RequestSearchParams,
  SearchParameters,
  SearchParams,
  SearchResultResponse,
  SortingOrderType,
} from 'src/app/interfaces/search.interface';
import {
  Communities,
  Community,
  FullUser,
  OrganizationLink,
  Project,
  Projects,
  SearchResult,
} from 'src/app/swagger-api';
import { MaybeUndefined } from 'src/app/utils';
import { OptionsProjectCommunitiesParameter, OrganizationInfo } from '../../../interfaces/profile.interface';
import FieldAllowedAttributeTypes from '../field-allowed-attribute-types.values';
import FieldAllowedValueClasses from '../field-allowed-value-classes.values';

@Injectable({ providedIn: 'root' })
export class SelectorOptionsService implements OnDestroy {
  public get optionsSubject(): Observable<OptionsChangesContent> {
    return this.optionsChanges$.asObservable();
  }

  public get paginationState(): Observable<PaginationState> {
    return this.paginationChanged$.pipe(startWith(INITIAL_PAGINATION_STATE));
  }

  public get isLoading(): Observable<boolean> {
    return this.isLoading$.pipe(startWith(false));
  }

  private static getSortedOptions(options: SelectorOption[]): SelectorOption[] {
    return options.sort((a: SelectorOption, b: SelectorOption) => a.title.localeCompare(b.title));
  }

  public allItems: OrganizationInfo[] = [];

  private accResult: SearchResultResponse;
  private optionsChanges$: Subject<OptionsChangesContent> = new Subject();
  private resultReceived$: Subject<SelectorOption[]> = new Subject();
  private paginationChanged$: Subject<PaginationState> = new Subject();
  private isLoading$: Subject<boolean> = new Subject();

  private currentSearchParams: SearchParameters = { itemKind: SearchCategories.ALL_CATEGORIES, queryText: '' };

  private subscriptions: SubscriptionLike[] = [];

  private readonly processorKinsMapper: {
    [key: string]: DropdownFieldsProcessorKind;
  } = {
    [FieldAllowedValueClasses.ORGANIZATIONS_TYPE_INPUT_SELECT]: DropdownFieldsProcessorKind.editable_selector,
    [FieldAllowedValueClasses.GENDER_SELECT]: DropdownFieldsProcessorKind.editable,
  };

  private readonly fieldSelectorTypes: {
    [key: string]: (attribute: FieldAttribute) => void;
  } = {
    [FieldAllowedValueClasses.PROJECTS_SEARCH_SELECT]: (attribute: FieldAttribute) => this.getProjects(attribute),
    [FieldAllowedValueClasses.COMMUNITIES_SEARCH_SELECT]: (attribute: FieldAttribute) =>
      this.getCommunities(attribute, 'linked_communities'),
    [FieldAllowedValueClasses.CONNECT_COMMUNITY_SEARCH_SELECT]: (attribute: FieldAttribute) =>
      this.getCommunities(attribute),
    [FieldAllowedValueClasses.ORGANIZATIONS_TYPE_INPUT_SELECT]: (attribute: FieldAttribute) =>
      this.getSuggestionsFromOptions(attribute),
    [FieldAllowedValueClasses.GENDER_SELECT]: (attribute: FieldAttribute) => this.getSuggestionsFromOptions(attribute),
    [FieldAllowedValueClasses.SUFFIX_SELECT]: (attribute: FieldAttribute) => this.getSuggestionsFromOptions(attribute),
    [FieldAllowedValueClasses.SEARCH_SELECT]: (attribute: FieldAttribute) => this.getSuggestionsFromOptions(attribute),
    [FieldAllowedValueClasses.IS_OPEN_SELECT]: (attribute: FieldAttribute) => this.getSuggestionsFromOptions(attribute),
    [FieldAllowedValueClasses.YES_NO_SELECT]: (attribute: FieldAttribute) => this.getSuggestionsFromOptions(attribute),
  };

  private searchResponseMapper: {
    [key in SearchParameterOptions]: (items: SearchResultResponse) => SelectorOption[];
  } = {
    [SearchParameterOptions.linked_community_ids_select]: (items: SearchResultResponse) =>
      this.getCommunitiesProjects(items),
    [SearchParameterOptions.linked_project_ids_select]: (items: SearchResultResponse) =>
      this.getCommunitiesProjects(items),
    [SearchParameterOptions.linked_organization_ids_select]: (items: SearchResultResponse) =>
      this.getOrganizations(items),
  };

  public constructor(
    private projectService: ProjectsService,
    private communitiesService: CommunitiesService,
    private searchService: SearchService,
    private route: ActivatedRoute,
  ) {}

  public ngOnDestroy(): void {
    completeSubject(this.optionsChanges$);
    completeSubject(this.resultReceived$);
    completeSubject(this.isLoading$);
    completeSubject(this.paginationChanged$);
    this.unsubscribe();
  }

  /**
   * @description Selects and calls the needed method depending on backendAttribute value_class_front.
   * @param { FieldAttribute } attribute Field attributes metadata.
   */
  public initOptions(attribute: FieldAttribute): void {
    const selectorType: MaybeUndefined<(attribute: FieldAttribute) => void> =
      this.fieldSelectorTypes[attribute.backendAttribute.value_class_front];

    if (!selectorType) {
      console.error('Unsupported filed attribute');
      return;
    }

    selectorType(attribute);
  }

  /**
   * @description Returns field's value processor kind.
   *    1. If the control with specified value_class_front is one of the
   *       processorKinsMapper types, returns it.
   *    2. Otherwise returns default kind.
   * @param { FieldAttribute } attribute Field attributes metadata.
   * @returns { DropdownFieldsProcessorKind }
   */
  public getProcessorType(attribute: FieldAttribute): DropdownFieldsProcessorKind {
    return (
      this.processorKinsMapper[attribute.backendAttribute.value_class_front] ?? DropdownFieldsProcessorKind.default
    );
  }

  /**
   * @description Checks if the current control has the specified name.
   * @param { FieldAttribute } attribute Field attributes metadata.
   * @param { OptionsChangesContent } content Contains control name and suggestions.
   * @returns { boolean }
   */
  public isCurrentField(attribute: FieldAttribute, content: OptionsChangesContent): boolean {
    return attribute.backendAttribute.name === content.fieldName;
  }

  /**
   * @description Gets triggered upon changing multiple_suggestions_search_select and
   *              multiple_search_select input values. Depending on input type initiates
   *              API request to fetch list of options or gets them locally.
   * @param fieldAttribute
   * @param searchParams
   * @param selectedItems
   * @returns { Observable<SelectorOption[]> } updated options
   */
  public getUpdatedOptions(
    fieldAttribute: FieldAttribute,
    searchParams: SearchParams,
    selectedItems: (OrganizationLink | SelectorOption)[],
  ): Observable<SelectorOption[]> {
    const { searchTerm, pagination } = searchParams;

    this.currentSearchParams = this.getSearchOptions(searchTerm, fieldAttribute);

    if (
      fieldAttribute.backendAttribute.type_attribute !== FieldAllowedAttributeTypes.MULTIPLE_SUGGESTIONS_SEARCH_SELECT
    ) {
      return of(this.filteredValues(fieldAttribute.options ?? [], searchTerm, selectedItems as SelectorOption[]));
    }

    const params: RequestSearchParams = {
      state: {
        params: this.currentSearchParams,
        pagination: { ...pagination, limit: FieldsConstants.PAGINABLE_SEARCH_RESULT_COUNT },
        sortParams: { ascending: false, orderType: SortingOrderType.SCORE },
      },
      applyLocationAttrs: true,
    };

    this.searchService.setStrategy(
      (FieldsSearchStrategy[fieldAttribute.backendAttribute.value_class_front] as SearchStrategyTypes) ||
        SearchStrategyTypes.DEFAULT,
    );

    this.isLoading$.next(true);
    this.subscriptions.push(
      this.searchService
        .getSearchResults(params)
        .pipe(
          finalize(() => this.isLoading$.next(false)),
          map((response: SearchResultResponse) => ({
            ...response,
            search_results: response.search_results.filter((result: SearchResult) => result.view_type !== 'deleted'),
          })),
          map((result: SearchResultResponse) => {
            return result.pagination.currentPage > 1 ? accumulateResult(this.accResult, result) : result;
          }),
        )
        .subscribe((result: SearchResultResponse) => {
          this.accResult = result;

          this.allItems = result.search_results.map((item: SearchResult) => {
            const fullUser: FullUser = item.item as FullUser;
            return {
              name: fullUser.displayed_name,
              userId: fullUser.user_id,
            };
          });

          const searchOptions: SearchParameterOptions = SearchParameterOptions[
            fieldAttribute.backendAttribute.value_class_front
          ] as SearchParameterOptions;

          const mapper: (items: SearchResultResponse, searchTerm: string) => SelectorOption[] = objectValue(
            this.searchResponseMapper,
            searchOptions,
          );

          this.resultReceived$.next(this.dropOutSelected(mapper(result, searchTerm), selectedItems));

          this.paginationChanged$.next({
            totalItemsCount: result.total_matches_count,
            currentPage: result.pagination.currentPage,
          });

          this.unsubscribe();
        }),
    );

    return this.resultReceived$.asObservable();
  }

  /**
   * @description Filters options based on available values dropping already selected ones.
   * @param { SelectorOption[] } options
   * @param { string } term
   * @param selectedItems
   * @returns { SelectorOption[] } values
   */
  public filteredValues(options: SelectorOption[], term: string, selectedItems: SelectorOption[]): SelectorOption[] {
    const regexp = new RegExp(term.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1'), 'gi');

    return options
      ? SelectorOptionsService.getSortedOptions(
          options.filter(
            (item: SelectorOption) =>
              !selectedItems.find(
                (selectOption: SelectorOption) =>
                  selectOption.title === item.title || selectOption.entity_id === item.entity_id,
              ) &&
              (!term.length || item.title.match(regexp)),
          ),
        )
      : [];
  }

  private getSearchOptions(term: string, fieldAttribute: FieldAttribute): SearchParameters {
    return {
      queryText: term,
      itemKind: SearchParameterOptions[fieldAttribute.backendAttribute.value_class_front] as SearchCategories,
    };
  }

  private dropOutSelected(
    fetchedItems: (SelectorOption | OrganizationLink)[],
    selectedItems: (SelectorOption | OrganizationLink)[],
  ): SelectorOption[] {
    const selectedIds: string[] = selectedItems
      .map((selectedItem) => {
        if ('entity_id' in selectedItem) {
          return selectedItem.entity_id;
        }

        if ('organization_user_id' in selectedItem) {
          return selectedItem.organization_user_id;
        }

        return undefined;
      })
      .filter((item: string | undefined): item is string => Boolean(item));

    const paramRoute: string | null = this.route.snapshot.paramMap.get('id');

    if (paramRoute) {
      selectedIds.push(paramRoute);
    }

    return fetchedItems.filter(
      (item: SelectorOption | OrganizationLink): item is SelectorOption =>
        'entity_id' in item && !selectedIds.includes(item.entity_id),
    );
  }

  /**
   * @description Notifies the subscribers about updated options after its calculating.
   * @param { (Project | Community)[] } items
   * @param { FieldAttribute } attribute
   * @param { string } propName
   */
  private emitConvertedItems(parameterOptionsProjectCommunities: OptionsProjectCommunitiesParameter): void {
    const items: (Project | Community)[] = parameterOptionsProjectCommunities['items'];
    let options: SelectorOption[] = this.convertItemsToOptions(
      items,
      parameterOptionsProjectCommunities['propName'],
      parameterOptionsProjectCommunities['isLinkedShow'],
    );

    options = options.filter(
      (option: SelectorOption, index: number, self: SelectorOption[]) =>
        index === self.findIndex((anotherOption: SelectorOption) => option.entity_id === anotherOption.entity_id),
    );

    options = SelectorOptionsService.getSortedOptions(options);

    this.optionsChanges$.next({
      options,
      fieldName: parameterOptionsProjectCommunities['attribute'].backendAttribute.name,
    });
  }

  /**
   * @description Prepares the combined list of entities and its linked entities.
   * @param { (Project | Community)[] } items
   * @param { string } propName
   * @param isLinkedShow
   */
  private convertItemsToOptions(
    items: (Project | Community)[],
    propName?: string,
    isLinkedShow = false,
  ): SelectorOption[] {
    let options: SelectorOption[] = [];
    items.forEach((entity: Project | Community) => {
      options.push({
        entity_id: entity.entity_id,
        title: entity.title,
        kind: OrganizationLink.OrganizationLinkKindEnum.CUSTOM,
      });
      if (isLinkedShow && propName) {
        options = options.concat(
          (entity[propName] as (Project | Community)[]).map((item: Project | Community) => ({
            entity_id: item.entity_id,
            title: item.title,
            kind: OrganizationLink.OrganizationLinkKindEnum.CUSTOM,
          })),
        );
      }
    });

    return options;
  }

  /**
   * @description Fetches users projects for suggestions list and notifies the subscribed
   *              control about options changes.
   * @param { FieldAttribute } attribute Field attributes metadata.
   * @param isLinkedShow
   */
  private getProjects(attribute: FieldAttribute, isLinkedShow = false): void {
    this.projectService.socialGroupChanges.subscribe((projects: Projects) => {
      const parameterOptionsProjectCommunities: OptionsProjectCommunitiesParameter = {
        items: projects.items,
        attribute,
        propName: 'linked_projects',
        isLinkedShow,
      };
      this.emitConvertedItems(parameterOptionsProjectCommunities);
    });

    this.projectService.fetchPersonalEntities();
  }

  /**
   * @description Fetches users communities for suggestions list and notifies the subscribed
   *              control about options changes.
   * @param { FieldAttribute } attribute Field attributes metadata.
   * @param { string } propName Nested prop name. Optional.
   * @param isLinkedShow
   */
  private getCommunities(attribute: FieldAttribute, propName = '', isLinkedShow = false): void {
    this.communitiesService.socialGroupChanges.subscribe((communities: Communities) => {
      const parameterOptionsProjectCommunities: OptionsProjectCommunitiesParameter = {
        items: communities.items,
        attribute,
        propName,
        isLinkedShow,
      };
      this.emitConvertedItems(parameterOptionsProjectCommunities);
    });

    this.communitiesService.fetchPersonalEntities();
  }

  /**
   * @description Converts attribute options to suggestions list and the subscribed
   *              control about options changes.
   * @param { FieldAttribute } attribute Field attributes metadata.
   */
  private getSuggestionsFromOptions(attribute: FieldAttribute): void {
    this.optionsChanges$.next({
      options: attribute.options ?? [],
      fieldName: attribute.backendAttribute.name,
    });
  }

  private getCommunitiesProjects(items: SearchResultResponse): SelectorOption[] {
    return items.search_results.map((result: SearchResult) => ({
      entity_id: (result.item as Community).entity_id,
      title: (result.item as Community).title,
      kind: OrganizationLink.OrganizationLinkKindEnum.CUSTOM,
    }));
  }

  private getOrganizations(items: SearchResultResponse): SelectorOption[] {
    return items.search_results.map((result: SearchResult) => ({
      entity_id: (result.item as FullUser).user_id,
      title: this.generateTitle(result.item as FullUser),
      kind: OrganizationLink.OrganizationLinkKindEnum.CUSTOM,
    }));
  }

  private generateTitle(item: FullUser): string {
    const keyword = this.currentSearchParams.queryText;

    const name = item.organization?.alternative_names?.find((item) =>
      item.toLowerCase().includes(keyword.toLowerCase()),
    );

    if (keyword && name) {
      return item.displayed_name + ` (${name})`;
    }

    return item.displayed_name;
  }

  private unsubscribe(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach((subscription: SubscriptionLike, index: number) => {
        if (index < this.subscriptions.length - 1) {
          subscription.unsubscribe();
        }
      });
    }
  }
}
